import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { EventsService } from 'src/app/services/event/event.service';
import { UserSessionService } from 'src/app/services/user-session/user-session.service';
import { Navigator } from 'src/app/components/menu/navigator';
import { SuccessMessageService } from 'src/app/services/message-provider/success-message.service';
import { LoginService } from 'src/app/services/login/login.service';
import { BiometricService } from 'src/app/services/biometric/biometric.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { UniqueIDService } from 'src/app/services/unique-id/unique-id.service';
import { Platform, AlertController, IonContent, ToastController } from '@ionic/angular';
import { AppLinksService } from 'src/app/services/applinks/app-links.service';
import { GlobalErrorHandlerService } from 'src/app/services/global-errors/global-error-handler.service';
import { EMAIL_PATTERN, mustBeTruthy } from 'src/app/services/customFormValidators';
import Cookies from "js-cookie";
import { LocationWarningService } from 'src/app/services/location-warning/location-warning.service';
import { SiteContentService } from 'src/app/services/sitecontent/sitecontent.service';
import { SiteContentModel } from 'src/app/models/sitecontent-model';

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
})

export class LoginComponent implements OnInit {

	@ViewChild(IonContent, { static: false }) content: IonContent;

	public doLoginInProgress: boolean = false;
	public email: string;
	public emailisSent: boolean = false;
	public showResetTitle: boolean = false;
	public error: GlobalErrorHandlerService = null;
	public loginResponse: any;
	public tooManyLoginAttemps: boolean = false;
  	public hideBoydRewards: boolean = false;

	public loginForm: FormGroup;
	public clientapp = new FormControl('')
	public csrf = new FormControl('');
	public username = new FormControl('', [
		Validators.required,
		Validators.pattern(EMAIL_PATTERN),
		Validators.maxLength(250),
	]);
	public password = new FormControl('');
	public loginAgreeFieldCheck: boolean;
	public loginAgreeField = new FormControl(({
		value: false,
		disabled: this.configService.getConfig().FEATURE_LOGIN_AGREE_FIELD !== '1' ? true : false
	}), [mustBeTruthy]);
	public tapCount: number = 0;
	public tapTimeoutID: any = 0;
	public showDeviceId: boolean = false;
	public deviceId: any;
	public MFAId: any;
	public configLabels: any = [];
	public resetPasswordType: any;

	@Input() biometricVerificationFailed = false;
	constructor(
		private authenticationService: AuthenticationService,
		private userSessionService: UserSessionService,
		private loginService: LoginService,
		public biometricService: BiometricService,
		private eventsService: EventsService,
		private configService: ConfigService,
		private navigator: Navigator,
		private uniqueID: UniqueIDService,
		private globalErrorHandlerService: GlobalErrorHandlerService,
		public alertController: AlertController,
		public platform: Platform,
		public successMessageService: SuccessMessageService,
		public appLinksService: AppLinksService,
		public toastController: ToastController,
		private siteContentService: SiteContentService,
		private locationWarningService: LocationWarningService
	) {
		this.configLabels = new SiteContentModel();
	}

	ngOnInit() {

		this.resetPasswordType = this.configService.getConfig().FEATURE_PASSWD_RESET_STEP;
		this.loginForm = new FormGroup({
			csrf: this.csrf,
			username: this.username,
			password: this.password,
			clientapp: this.clientapp,
			agreeLogin: this.loginAgreeField
		});
		this.getMFAId();
		this.getDeviceId();
		this.generateCsrfToken();
    if (this.configService.getConfig().BOYD_REWARDS_HIDE === '1') {
      this.hideBoydRewards = true;
    }
		if (this.configService.isBiometricEnabled() && this.platform.is('capacitor') && !this.biometricVerificationFailed)
			this.initiateBiometric();

		this.loginAgreeFieldCheck = this.configService.getConfig().FEATURE_LOGIN_AGREE_FIELD === '1' ? true : false;
	}

	ngOnDestroy() {
		this.biometricService.verificationError = null;
	}

	async initiateBiometric() {
		this.biometricService.verificationError = null;
		await this.biometricService.isAvailable();
		await this.biometricService.verifyUser();
		this.doLoginBiometric()
	}

	generateCsrfToken() {
		this.authenticationService.getCsrfToken().subscribe((response) => {
			if (!!response.payload && !!response.payload.token) {
				this.loginForm.controls['csrf'].setValue(response.payload.token);
			}
		});
	}

	ionViewDidEnter() {
		this.successMessageService.clearMessage();
	}

	ngAfterViewInit(){
		this.siteContentService.loadConfig(this.configService.getConfig().FEATURE_MULTIPLE_PD_FAILURE_CONFIG)
		.then((response: any) => {
		  this.configLabels = response;
		})
	}


	async doLogin() {
		this.doLoginInProgress = true;
		this.error = null;

		this.email = this.loginForm.get('username').value;

		if (!this.loginForm.valid) return console.log('Form validation failed');

		this.loginForm.get("clientapp").setValue(this.configService.getClientApp());
		const value = this.loginForm.value;
		const device = this.MFAId;
		value.device_id = device;
		const fbp = Cookies.get('_fbp');
		const fbc = Cookies.get('_fbc');

		if (typeof fbp !== 'undefined') {
			value.deviceDetails = fbp;
		}
		if (typeof fbc !== 'undefined') {
			value.externalSource = fbc;
		}

		this.loginService.loginCustomer(value).subscribe({
			next: (response: any) => {

				this.doLoginInProgress = false;

				if (response.result === 0) return this.doLoginFailProtocol(response.code);

				this.userSessionService.setSessionToken(response.sessionToken);
				this.navigator.dismiss();

				if (this.configService.isBiometricEnabled() && this.biometricService.useBiometric) this.biometricService.setCredentials(value);

				this.loginService.setLoginResponseInStorage();
        this.navigator.dismiss();
			},
			error: (error) => {
				this.error = error;
				this.doLoginFailProtocol(error.getErrorCode().toUpperCase());
			}
		});
	}

	doLoginFailProtocol(errorCode: string) {
		this.generateCsrfToken();
		this.content.scrollToTop(10);

		if (!this.error || !(this.error instanceof GlobalErrorHandlerService)) {
			this.error = this.globalErrorHandlerService;
		}

		switch (errorCode) {
			case 'LOGIN_WRONG_USERNAME':
				this.error.setErrorMessage('An error has occured logging you in. Please try again.');
				this.username.setErrors({ LOGIN_WRONG_USERNAME: true });
				this.username.markAsDirty();
				this.username.markAsTouched();
				break;
			case 'LOGIN_WRONG_PASSWORD':
				this.error.setErrorMessage('An error has occured logging you in. Please try again.');
				this.password.setErrors({ LOGIN_WRONG_PASSWORD: true });
				this.password.markAsDirty();
				this.password.markAsTouched();
				break;
			case 'FORCE_PASSWORD_RESET':
				this.error.setErrorMessage("An e-mail has been sent to you. Please follow the instructions to reset your password.");
				this.startMigrationProcess();
				break;
			case 'FAILED_TOO_MANY_ATTEMPTS':
				this.error.setErrorMessage("Too many Attemps, please contact support");
				break;
			case "LOGIN_MAX_LOGINS_REACHED":
				console.log("test ", this.configLabels);
				// this.error.setErrorMessage("Your account was disabled upon too many failed logins, please contact support.");
				this.error.setErrorMessage(this.configLabels.error_message);
				this.tooManyLoginAttemps = true;
				break;
			case 'TIME_LIMIT_EXCEEDED':
				this.error.setErrorMessage('Time limit exceeded.');
				break;
			case 'NOT_AUTHORIZED_CLIENT_APP':
				this.error.setErrorMessage('You are logging from an unauthorized client app version.');
				break;
			case 'CUSTOMER_EXCLUDED_BY_AUTHORITY':
				this.error.setErrorMessage('Your account has been excluded from this site. Please contact the State of ' +
					this.configService.getState() + ' Division of Gaming Enforcement for further information');
				break;
			case 'CUSTOMER_WRONG_STATUS':
				this.error.setErrorMessage("Your account is not in 'Active' status. Please contact support.");
				break;
			case 'CUSTOMER_SELF_EXCLUDED':
				this.navigator.dismiss();
				this.navigator.openPopover("PopoverSelfExcludeComponent", "", 'standard-popover  self-exclude');
				break;
			case 'CUSTOMER_SELF_EXCLUDED_COOL_OFF':
				this.error.setErrorMessage('You have suspended yourself from play.');
				break;
			case 'CUSTOMER_SUSPENDED':
				this.error.setErrorMessage('Your account has been temporarily suspended. If this is not resolved in 24 hours please contact support.');
				break;
			default:
				this.error.setErrorMessage('An error has occured logging you in. Please try again.');
				break;
		}

		this.eventsService.publish('event:login:error', { errorCode: this.error.getErrorCode().toUpperCase(), errorName: this.error.getErrorMessage() });
	}

	public startMigrationProcess(): any {
		const clientApp = this.configService.getClientApp();
		this.authenticationService.callForgotPasswordApi(this.email, clientApp).subscribe({
			next: (response: any) => {
				if (!!response) this.showPasswordResetAlert();
			},
			error: (error) => {
				this.content.scrollToTop(10);
				this.error = error;
				console.error(this.error);
			}
		});
	}

	public showPasswordResetAlert(): any {
		const popoverComp = 'PopoverAlertComponent';

		const data: any = {
			content: "An e-mail has been sent to you. Please follow the instructions to reset your password.",
		};

		return this.navigator.openPopover(popoverComp, data);
	}

	async toggleBiometric() {
		await this.biometricService.onToggleUseBiometric()
		if (this.biometricService.useBiometric) this.doLoginBiometric()
	}

	async doLoginBiometric() {
		if (this.biometricService.verificationError) {
			//verfication failed
			await this.alertCreator(
				{
					header: `${this.biometricService.biometricType} verification failed`,
					message: 'Would you like to remove your saved credentials?',
					buttons: [
						{
							text: 'Yes',
							handler: async () => {
								await this.biometricService.deleteCredentials();
								await this.biometricService.setBiometricStatus(false);
							},
						},
						{
							text: 'No',
							handler: async () => {
								await this.biometricService.setBiometricStatus(true);
								this.biometricService.verificationError = null;
							},
						},
					],
				}
			)
		}

		if (this.biometricService.verified) {
			this.loginForm.controls['password'].setValue(this.biometricService.credentials.password);
			this.loginForm.controls['username'].setValue(this.biometricService.credentials.username);
			this.biometricService.verified = false;
			this.doLogin()
		}
	}

	public navigateTo(page: string): any {
		return this.navigator.navigateTo(page);
	}

	async alertCreator(alert) {
		const currentAlert = await this.alertController.create({
			header: alert.header,
			message: alert.message,
			buttons: alert.buttons
		});
		await currentAlert.dismiss();
		await currentAlert.present();
	}


	public onSendResetPasswordEMail(email): void {
		const clientApp = this.configService.getClientApp();
		const obj = {
			email: email,
			clientapp: clientApp
		}

		this.authenticationService.callForgotPassword(obj).subscribe({
			next: (response: any) => {
				this.emailisSent = true,
				this.showResetTitle = true;
				if (this.resetPasswordType && this.resetPasswordType == "MFA") {
				//redirect to next step MFA with email
					return this.navigator.navigateTo("ResetPasswordMFAEmailComponent", {}, {email: {email: email}});
				}
			},
			error: (error) => this.emailisSent = true
		});
	}

	showMessage() {
		const hoverItem = document.getElementsByClassName('message-box');
		for (let i = 0; i < hoverItem.length; i++) {
			hoverItem[i].className = hoverItem[i].className.replace('message-box', 'message-box-visible');
		}
	}

	iconHoverEndEmitterFit() {
		const hoverItem = document.getElementsByClassName('message-box-visible');
		for (let i = 0; i < hoverItem.length; i++) {
			hoverItem[i].className = hoverItem[i].className.replace('message-box-visible', 'message-box');
		}
	}

	openMatchedExternalLinks(code, title) {
		return this.appLinksService.openMatchedExternalLinksPopover(code, title)
	}

	onLogoTap() {
		if (this.platform.is('capacitor')) {
			this.tapCount += 1;
			clearTimeout(this.tapTimeoutID);

			if (this.tapCount >= 15) {
				this.navigator.toLandingPage(true);
			} else if (this.tapCount >= 10) {
				this.showDeviceId = true;
			}

			this.tapTimeoutID = setTimeout(() => {
				this.tapCount = 0;
			}, 7000);
		}
	}

	getDeviceId() {
		if (this.platform.is('capacitor')) {
			this.uniqueID.getSelectorID().then(
				response => {
					this.deviceId = response
				},
				reject => {
					this.deviceId = "Not available!"
				}
			);
		}
	}

	getMFAId() {
		this.uniqueID.getUniqueID().then(id => this.MFAId = id);
	}

	closeModal = () => {
		this.navigator.dismiss();
	}
	navigateToStepOne(){
		if(this.locationWarningService.locationIsAllowed()){
			this.navigator.navigateTo('SignupStepOneComponent',{  multipleModals: true },{connectBoydCardPath:false});
		}else {
			this.navigator.navigateTo('SignupStepOneComponent',{  multipleModals: true },{connectBoydCardPath:false});
			this.locationWarningService.openLocationWarning('join',{connectBoydCardPath:false});
		}
	}

	navigateToSignupOptions(){
		if(this.locationWarningService.locationIsAllowed()){
			this.navigator.navigateTo('SignupStepOneComponent',{  multipleModals: true },{connectBoydCardPath:false});
		}else {
			this.navigator.navigateTo('SignupStepOneComponent',{  multipleModals: true },{connectBoydCardPath:false});
			this.locationWarningService.openLocationWarning('join',{connectBoydCardPath:false});
		}
	}

}
